import { useState, useEffect } from 'react';

import Form from 'components/Form';
import { IFormRegister, IFormValues, IFormWatch } from 'routes/Main/type';

import RowContainer from '../../components/RowContainer';
import RowContent from '../../components/RowContent';
import SubRowContent from './RowContent';

import SectionTitle from '../../components/SectionTitle';

import { Box, MenuItem, FormControlLabel, Checkbox } from '@mui/material';

interface Props extends IFormRegister, IFormValues, IFormWatch {
  disabled?: boolean;
}

const OperationInfo = (props: Props) => {
  const { disabled, register, watch, setValue, getValues } = props;

  const [postureEtc, setPostureEtc] = useState(0);
  const [methodEtc, setMethodEtc] = useState(0);

  const position_labels = [
    'Nằm ngửa',
    'Nằm đầu thấp',
    'Nằm đầu cao',
    'Fowler’s',
    'Semi-Fowler’s',
    'phụ khoa',
    'Nằm nghiêngl (trái)',
    'Nằm nghiêng (phải)',
    'nằm sấp',
    'Jackknife',
  ];
  const method_labels = ['Local', 'General', 'Spinal', 'Epidural'];

  const contents = [
    {
      label: 'Ngày phẫu thuật',
      element: (
        <Form.MuiTextField
          type="date"
          InputLabelProps={{ shrink: true }}
          required={false}
          disabled={disabled}
          {...register('operation_information.operating_date')}
        />
      ),
    },
    {
      label: 'Chẩn Đoán sơ bộ',
      element: (
        <Form.MuiTextField
          {...register('operation_information.operating_department')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Chẩn đoán sau\nphẫu thuật',
      element: (
        <Form.MuiTextField
          {...register('operation_information.operating_time')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tên phẫu thuật',
      element: (
        <Form.MuiTextField
          {...register('operation_information.operation_name')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Vị trí',
      element: (
        <Form.MuiTextField
          {...register('operation_information.npo_status')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tình trạng da',
      element: (
        <Form.MuiTextField
          {...register('operation_information.emergency_status')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Chuẩn bị da',
      element: (
        <Form.MuiTextField
          {...register('operation_information.prophylactic_method')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tư thế',
      element: (
        <Box display="flex">
          <Form.MuiTextField
            select
            required={false}
            disabled={disabled}
            defaultValue={
              [...position_labels, undefined].includes(
                getValues('operation_information.position')
              )
                ? getValues('operation_information.position')
                : 'etc'
            }
            {...register('operation_information.position')}
            onChange={e => {
              if (e.target.value === 'etc') setPostureEtc(1);
              else setPostureEtc(0);
            }}
          >
            {position_labels.map(v => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
            <MenuItem value="etc">Nhập trực tiếp</MenuItem>
          </Form.MuiTextField>
          {postureEtc ? (
            <Form.MuiTextField
              {...register('operation_information.position_etc')}
              required={false}
              disabled={disabled}
              placeholder="Nhập"
              sx={{ marginLeft: '5px' }}
            />
          ) : null}
        </Box>
      ),
    },

    {
      label: 'NPO',
      element: (
        <Form.MuiTextField
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues('operation_information.asa_class')}
          {...register('operation_information.asa_class')}
        >
          <MenuItem value="NPO">NPO</MenuItem>
          <MenuItem value="None">Không NPO</MenuItem>
        </Form.MuiTextField>
      ),
    },
    {
      label: 'Kháng sinh\ndự phòng',
      element: (
        <Form.MuiTextField
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues(
            'operation_information.prophylactic_antibiotics'
          )}
          {...register('operation_information.prophylactic_antibiotics')}
        >
          <MenuItem value="injection">Tiêm</MenuItem>
          <MenuItem value="none">Không</MenuItem>
        </Form.MuiTextField>
      ),
    },
    {
      label: 'Dị ứng',
      element: (
        <Box display="flex">
          <Form.MuiTextField
            {...register('operation_information.operating_allergy')}
            required={false}
            disabled={disabled}
            sx={{ width: '37%' }}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                defaultChecked={getValues(
                  'operation_information.preoperative_xray'
                )}
                {...register('operation_information.preoperative_xray')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2D73DC',
                  },
                }}
              />
            }
            label="Pre X-ray"
            sx={{ marginLeft: '1vw' }}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                defaultChecked={getValues(
                  'operation_information.preoperative_ekg'
                )}
                {...register('operation_information.preoperative_ekg')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2D73DC',
                  },
                }}
              />
            }
            label="Pre EKG"
            sx={{ marginLeft: '8vw' }}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (
      ![...position_labels, undefined].includes(
        getValues('operation_information.position')
      )
    ) {
      setValue(
        'operation_information.position_etc',
        getValues('operation_information.position')
      );
      setPostureEtc(1);
    }

    if (
      ![...method_labels, undefined].includes(
        getValues('operation_information.prophylactic_method')
      )
    ) {
      setValue(
        'operation_information.prophylactic_method_etc',
        getValues('operation_information.prophylactic_method')
      );
      setMethodEtc(1);
    }
  }, []);

  return (
    <>
      <SectionTitle title="Thông tin phẫu thuật" />
      <RowContainer xs={12}>
        {contents.map(({ label, element }) => {
          if (label === 'Dị ứng') {
            return (
              <>
                <RowContent title={label} titleRatio={1} childrenRatio={5}>
                  {element}
                </RowContent>
              </>
            );
          } else if (label === 'Tư thế') {
            return (
              <>
                <RowContent title={label} titleRatio={1} childrenRatio={2}>
                  {element}
                </RowContent>
              </>
            );
          } else {
            return (
              <>
                <SubRowContent title={label} titleRatio={1} childrenRatio={2}>
                  {element}
                </SubRowContent>
              </>
            );
          }
        })}
      </RowContainer>
    </>
  );
};

export default OperationInfo;
